import axiosInstance from "./httpCommon3";
import { currentUserCode } from "./ccOnboardingApi";

export const LAB_ONBOARDING_BASE = "/collection-center/onboarding";

function qs(params = {}) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      usp.set(key, value);
    }
  });
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result;
      const b64 = typeof result === "string" ? result.split(",")[1] : "";
      resolve(b64);
    };
    reader.onerror = (error) => reject(error);
  });
}

export function currentUserRole() {
  try {
    const loginObj = JSON.parse(localStorage.getItem("loginObj"));
    return (
      loginObj?.userSession?.role ||
      loginObj?.user?.role ||
      localStorage.getItem("role") ||
      "DE"
    );
  } catch {
    return localStorage.getItem("role") || "DE";
  }
}

/**
 * Format form payload clean of non-serializable File objects
 */
export function formatLabPayload(form, remarks = "") {
  const cleanBasic = {
    labName: form.basic?.labName || "",
    mobileNumber: form.basic?.mobileNumber || "",
    receptionNumber: form.basic?.receptionNumber || "",
    type: form.basic?.type || "",
    location: form.basic?.location || "",
    latitude: form.basic?.latitude ? String(form.basic.latitude) : "",
    longitude: form.basic?.longitude ? String(form.basic.longitude) : "",
    address: form.basic?.address || "",
    area: form.basic?.area || "",
    city: form.basic?.city || "",
    samplifyCityCode: form.basic?.samplifyCityCode || "",
    state: form.basic?.state || "",
    stateCode: form.basic?.stateCode || "",
    pinCode: form.basic?.pinCode || "",
    gstNumber: form.basic?.gstNumber || "",
    nablNumber: form.basic?.nablNumber || "",
    panNumber: form.basic?.panNumber || "",
    paymentPeriod: form.basic?.paymentPeriod || "monthly",
    paymentReconciliation: form.basic?.paymentReconciliation || "30",
    agreementDate: form.basic?.agreementDate || "",
    terminationNoticePeriod: form.basic?.terminationNoticePeriod ? String(form.basic.terminationNoticePeriod) : "30",
    isCureBayLabCenter: form.basic?.isCureBayLabCenter || "no",
    description: form.basic?.description || "",
  };

  const cleanTimings = (form.timings || []).map((t, idx) => ({
    id: t.id || `timing-${idx + 1}`,
    days: Array.isArray(t.days) ? t.days : [],
    opensAt: t.opensAt || "08:00",
    closesAt: t.closesAt || "20:00",
    isClosed: Boolean(t.isClosed),
  }));

  const cleanContacts = (form.contacts || []).map((c, idx) => ({
    id: c.id || `contact-${idx + 1}`,
    salutation: c.salutation || "",
    firstName: c.firstName || "",
    lastName: c.lastName || "",
    gender: c.gender || "Male",
    mobileNumber: c.mobileNumber || "",
    email: c.email || "",
    dob: c.dob || "",
    designation: c.designation || "",
    qualification: c.qualification || "",
    source: c.source || "",
    panNumber: c.panNumber || "",
  }));

  const cleanTestCities = (form.testCities || []).map((tc, idx) => ({
    id: tc.id || `city-${idx + 1}`,
    city: tc.city || "",
    files: [], // file instances are handled via draft attachments
  }));

  const cleanBank = {
    bankName: form.bank?.bankName || "",
    accountName: form.bank?.accountName || "",
    accountNumber: form.bank?.accountNumber || "",
    ifscCode: form.bank?.ifscCode || "",
    location: form.bank?.location || "",
  };

  return {
    basic: cleanBasic,
    timings: cleanTimings,
    contacts: cleanContacts,
    testCities: cleanTestCities,
    bank: cleanBank,
    notes: form.notes || "",
    remarks: remarks || form.remarks || "Submitted from the new Lab onboarding screen",
  };
}

const labOnboardingApi = {
  /**
   * 1. Save new Lab draft
   * POST /collection-center/onboarding/lab/draft?userCode={user_code}
   */
  saveNewLabDraft: ({ form, userCode, remarks }) => {
    const uCode = userCode || currentUserCode();
    const payload = formatLabPayload(form, remarks);
    return axiosInstance.post(
      `${LAB_ONBOARDING_BASE}/lab/draft${qs({ userCode: uCode })}`,
      payload
    );
  },

  /**
   * 2. Resume Lab draft
   * GET /collection-center/onboarding/lab/draft/{draft_key}
   */
  resumeLabDraft: (draftKey) =>
    axiosInstance.get(`${LAB_ONBOARDING_BASE}/lab/draft/${draftKey}`),

  /**
   * 3. Update saved Lab draft
   * PUT /collection-center/onboarding/lab/draft/{draft_key}?userCode={user_code}
   */
  updateSavedLabDraft: ({ draftKey, form, userCode, remarks }) => {
    const uCode = userCode || currentUserCode();
    const payload = {
      form: formatLabPayload(form, remarks),
      remarks: remarks || "Updated draft",
    };
    return axiosInstance.put(
      `${LAB_ONBOARDING_BASE}/lab/draft/${draftKey}${qs({ userCode: uCode })}`,
      payload
    );
  },

  /**
   * 4. Upload/Replace Draft Attachment
   * POST /collection-center/onboarding/draft/{draft_key}/attachment?onboardingType=LAB&stepNo={stepNo}&userCode={user_code}
   */
  uploadDraftAttachment: async ({
    draftKey,
    stepNo,
    description,
    file,
    fileName,
    contentType,
    base64,
    targetAttachmentId,
    userCode,
  }) => {
    if (!draftKey) throw new Error("draftKey is required to upload draft attachment");
    const uCode = userCode || currentUserCode();

    let b64 = base64;
    let name = fileName;
    let type = contentType;

    if (file && file instanceof File) {
      b64 = await toBase64(file);
      name = fileName || file.name;
      type = contentType || file.type || "application/octet-stream";
    }

    if (!b64) return null;

    const body = {
      description,
      fileName: name,
      contentType: type,
      base64: b64,
    };

    if (targetAttachmentId) {
      body.targetAttachmentId = targetAttachmentId;
    }

    return axiosInstance.post(
      `${LAB_ONBOARDING_BASE}/draft/${draftKey}/attachment${qs({
        onboardingType: "LAB",
        stepNo,
        userCode: uCode,
      })}`,
      body
    );
  },

  /**
   * 5. List active draft attachments
   * GET /collection-center/onboarding/draft/{draft_key}/attachment
   */
  listDraftAttachments: (draftKey) =>
    axiosInstance.get(`${LAB_ONBOARDING_BASE}/draft/${draftKey}/attachment`),

  /**
   * 6. Delete draft attachment
   * DELETE /collection-center/onboarding/draft/{draft_key}/attachment/{attachment_id}?userCode={user_code}
   */
  deleteDraftAttachment: ({ draftKey, attachmentId, userCode }) => {
    const uCode = userCode || currentUserCode();
    return axiosInstance.delete(
      `${LAB_ONBOARDING_BASE}/draft/${draftKey}/attachment/${attachmentId}${qs({
        userCode: uCode,
      })}`
    );
  },

  /**
   * 7. Submit saved Lab draft
   * POST /collection-center/onboarding/lab/draft/{draft_key}/submit?userCode={user_code}&role={role}
   */
  submitSavedLabDraft: ({ draftKey, form, userCode, role, remarks }) => {
    const uCode = userCode || currentUserCode();
    const r = role || currentUserRole();
    const payload = formatLabPayload(form, remarks);
    return axiosInstance.post(
      `${LAB_ONBOARDING_BASE}/lab/draft/${draftKey}/submit${qs({
        userCode: uCode,
        role: r,
      })}`,
      payload
    );
  },

  /**
   * 8. Get submitted Lab detail
   * GET /collection-center/onboarding/lab/{center_code}/detail
   */
  getSubmittedLabDetail: (centerCode) =>
    axiosInstance.get(`${LAB_ONBOARDING_BASE}/lab/${centerCode}/detail`),

  /**
   * 9. Get city list for state
   * GET /api/simplify/location/cityList?stateCode={stateCode}
   */
  getSimplifyCityList: (stateCode) =>
    axiosInstance.get(`/api/simplify/location/cityList`, {
      params: { stateCode },
    }),

  /**
   * 10. Update submitted Lab record
   * PUT /collection-center/onboarding/{center_code}?userCode={user_code}&draftKey={draft_key}
   */
  updateSubmittedLab: ({ centerCode, draftKey, form, userCode, remarks }) => {
    const uCode = userCode || currentUserCode();
    const formatted = formatLabPayload(form, remarks);
    const payload = {
      ...formatted,
      form: formatted,
      remarks: remarks || form?.notes || "Updated lab details",
    };
    return axiosInstance.put(
      `${LAB_ONBOARDING_BASE}/${centerCode}${qs({ draftKey, userCode: uCode })}`,
      payload
    );
  },
};

export default labOnboardingApi;
